import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { SERVER_URL } from "../databucket";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

const getUploadHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  };
};

const BASE_URL = `${SERVER_URL}/api/policy`;

// ── Async Thunks ──

export const fetchPolicyTypes = createAsyncThunk(
  "policyCheck/fetchPolicyTypes",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/types`, getAuthHeaders());
      return data.types;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to fetch policy types");
    }
  }
);

export const uploadPolicyDocument = createAsyncThunk(
  "policyCheck/uploadDocument",
  async ({ file, requestId, policyType }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("requestId", requestId);
      if (policyType) formData.append("policyType", policyType);

      const { data } = await axios.post(`${BASE_URL}/upload`, formData, getUploadHeaders());
      return data.document;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to upload document");
    }
  }
);

export const fetchPolicyDocuments = createAsyncThunk(
  "policyCheck/fetchDocuments",
  async (requestId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/documents/${requestId}`, getAuthHeaders());
      return data.documents;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to fetch documents");
    }
  }
);

export const fetchSingleDocument = createAsyncThunk(
  "policyCheck/fetchSingleDocument",
  async (documentId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/document/${documentId}`, getAuthHeaders());
      return data.document;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to fetch document");
    }
  }
);

export const confirmPolicyType = createAsyncThunk(
  "policyCheck/confirmType",
  async ({ documentId, policyType }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/confirm-type`,
        { documentId, policyType },
        getAuthHeaders()
      );
      return data.document;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to confirm type");
    }
  }
);

export const reparsePolicyDocument = createAsyncThunk(
  "policyCheck/reparse",
  async (documentId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${BASE_URL}/reparse/${documentId}`, {}, getAuthHeaders());
      return { documentId, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to reparse");
    }
  }
);

export const createChecklist = createAsyncThunk(
  "policyCheck/createChecklist",
  async ({ requestId, policyType, documentId, insuredName, client }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/checklist/create`,
        { requestId, policyType, documentId, insuredName, client },
        getAuthHeaders()
      );
      return data.checklist;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to create checklist");
    }
  }
);

export const fetchChecklists = createAsyncThunk(
  "policyCheck/fetchChecklists",
  async (requestId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/checklists/${requestId}`, getAuthHeaders());
      return data.checklists;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to fetch checklists");
    }
  }
);

export const fetchSingleChecklist = createAsyncThunk(
  "policyCheck/fetchSingleChecklist",
  async (checklistId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/checklist/${checklistId}`, getAuthHeaders());
      return data.checklist;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to fetch checklist");
    }
  }
);

export const updateChecklistItems = createAsyncThunk(
  "policyCheck/updateItems",
  async ({ checklistId, items }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${BASE_URL}/checklist/${checklistId}/items`,
        { items },
        getAuthHeaders()
      );
      return data.checklist;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to update items");
    }
  }
);

export const runComparison = createAsyncThunk(
  "policyCheck/runComparison",
  async (checklistId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/checklist/${checklistId}/compare`,
        {},
        getAuthHeaders()
      );
      return { checklist: data.checklist, summary: data.summary };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to run comparison");
    }
  }
);

export const runValidation = createAsyncThunk(
  "policyCheck/runValidation",
  async ({ checklistId, includeAIAnalysis = true }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/checklist/${checklistId}/validate`,
        { includeAIAnalysis },
        getAuthHeaders()
      );
      return data.validation;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to run validation");
    }
  }
);

export const updateChecklistStatus = createAsyncThunk(
  "policyCheck/updateStatus",
  async ({ checklistId, status }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${BASE_URL}/checklist/${checklistId}/status`,
        { status },
        getAuthHeaders()
      );
      return data.checklist;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to update status");
    }
  }
);

export const exportChecklist = createAsyncThunk(
  "policyCheck/exportChecklist",
  async (checklistId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/checklist/${checklistId}/export`,
        getAuthHeaders()
      );
      return data.export;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to export checklist");
    }
  }
);

export const attachPriorPolicy = createAsyncThunk(
  "policyCheck/attachPrior",
  async ({ checklistId, priorDocumentId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/checklist/${checklistId}/attach-prior`,
        { priorDocumentId },
        getAuthHeaders()
      );
      return data.checklist;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to attach prior policy");
    }
  }
);

export const comparePriorPolicy = createAsyncThunk(
  "policyCheck/comparePrior",
  async (checklistId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/checklist/${checklistId}/compare-prior`,
        {},
        getAuthHeaders()
      );
      return { checklist: data.checklist, summary: data.summary, aiSummary: data.aiSummary };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to compare with prior policy");
    }
  }
);

// ── ONE-CLICK: Upload + Parse + Compare in single call ──
export const oneClickCompare = createAsyncThunk(
  "policyCheck/oneClickCompare",
  async ({ requestId, oldPolicyFile, newPolicyFile, insuredName, client }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("requestId", requestId);
      if (oldPolicyFile) formData.append("oldPolicy", oldPolicyFile);
      if (newPolicyFile) formData.append("newPolicy", newPolicyFile);
      if (insuredName) formData.append("insuredName", insuredName);
      if (client) formData.append("client", client);

      const { data } = await axios.post(
        `${BASE_URL}/one-click-compare`,
        formData,
        getUploadHeaders()
      );
      return {
        checklist: data.checklist,
        comparisonSummary: data.comparisonSummary,
        aiSummary: data.aiSummary,
        documents: data.documents,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "AI analysis failed");
    }
  }
);

export const deletePolicyDocument = createAsyncThunk(
  "policyCheck/deleteDocument",
  async (documentId, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/document/${documentId}`, getAuthHeaders());
      return documentId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to delete document");
    }
  }
);

export const deleteChecklist = createAsyncThunk(
  "policyCheck/deleteChecklist",
  async (checklistId, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/checklist/${checklistId}`, getAuthHeaders());
      return checklistId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to delete checklist");
    }
  }
);

// ── Slice ──

const policyCheckSlice = createSlice({
  name: "policyCheck",
  initialState: {
    policyTypes: [],
    documents: [],
    checklists: [],
    activeChecklist: null,
    activeDocument: null,
    comparisonSummary: null,
    priorComparisonSummary: null,
    priorAISummary: "",
    validationResult: null,
    exportData: null,

    uploadStatus: "idle",
    parseStatus: "idle",
    compareStatus: "idle",
    priorCompareStatus: "idle",
    oneClickStatus: "idle", // idle | uploading | parsing | comparing | done | error
    oneClickStep: "",
    validateStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearActiveChecklist: (state) => {
      state.activeChecklist = null;
      state.comparisonSummary = null;
      state.validationResult = null;
    },
    clearActiveDocument: (state) => {
      state.activeDocument = null;
    },
    clearExportData: (state) => {
      state.exportData = null;
    },
    setActiveChecklist: (state, action) => {
      state.activeChecklist = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch policy types
      .addCase(fetchPolicyTypes.fulfilled, (state, action) => {
        state.policyTypes = action.payload;
      })

      // Upload document
      .addCase(uploadPolicyDocument.pending, (state) => {
        state.uploadStatus = "uploading";
        state.error = null;
      })
      .addCase(uploadPolicyDocument.fulfilled, (state, action) => {
        state.uploadStatus = "done";
        state.documents.unshift(action.payload);
      })
      .addCase(uploadPolicyDocument.rejected, (state, action) => {
        state.uploadStatus = "error";
        state.error = action.payload;
      })

      // Fetch documents
      .addCase(fetchPolicyDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPolicyDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchPolicyDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single document
      .addCase(fetchSingleDocument.fulfilled, (state, action) => {
        state.activeDocument = action.payload;
        // Also update in the list
        const idx = state.documents.findIndex((d) => d._id === action.payload._id);
        if (idx >= 0) state.documents[idx] = action.payload;
      })

      // Confirm type
      .addCase(confirmPolicyType.fulfilled, (state, action) => {
        state.activeDocument = action.payload;
        const idx = state.documents.findIndex((d) => d._id === action.payload._id);
        if (idx >= 0) state.documents[idx] = action.payload;
      })

      // Create checklist
      .addCase(createChecklist.fulfilled, (state, action) => {
        state.activeChecklist = action.payload;
        state.checklists.unshift(action.payload);
      })

      // Fetch checklists
      .addCase(fetchChecklists.fulfilled, (state, action) => {
        state.checklists = action.payload;
      })

      // Fetch single checklist
      .addCase(fetchSingleChecklist.fulfilled, (state, action) => {
        state.activeChecklist = action.payload;
      })

      // Update items
      .addCase(updateChecklistItems.fulfilled, (state, action) => {
        state.activeChecklist = action.payload;
        const idx = state.checklists.findIndex((c) => c._id === action.payload._id);
        if (idx >= 0) state.checklists[idx] = action.payload;
      })

      // Run comparison
      .addCase(runComparison.pending, (state) => {
        state.compareStatus = "comparing";
      })
      .addCase(runComparison.fulfilled, (state, action) => {
        state.compareStatus = "done";
        state.activeChecklist = action.payload.checklist;
        state.comparisonSummary = action.payload.summary;
        const idx = state.checklists.findIndex((c) => c._id === action.payload.checklist._id);
        if (idx >= 0) state.checklists[idx] = action.payload.checklist;
      })
      .addCase(runComparison.rejected, (state, action) => {
        state.compareStatus = "error";
        state.error = action.payload;
      })

      // Run validation
      .addCase(runValidation.pending, (state) => {
        state.validateStatus = "validating";
      })
      .addCase(runValidation.fulfilled, (state, action) => {
        state.validateStatus = "done";
        state.validationResult = action.payload;
      })
      .addCase(runValidation.rejected, (state, action) => {
        state.validateStatus = "error";
        state.error = action.payload;
      })

      // Update status
      .addCase(updateChecklistStatus.fulfilled, (state, action) => {
        state.activeChecklist = action.payload;
        const idx = state.checklists.findIndex((c) => c._id === action.payload._id);
        if (idx >= 0) state.checklists[idx] = action.payload;
      })

      // Export
      .addCase(exportChecklist.fulfilled, (state, action) => {
        state.exportData = action.payload;
      })

      // Delete document
      .addCase(deletePolicyDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter((d) => d._id !== action.payload);
        if (state.activeDocument?._id === action.payload) state.activeDocument = null;
      })

      // One-click compare
      .addCase(oneClickCompare.pending, (state) => {
        state.oneClickStatus = "uploading";
        state.oneClickStep = "Uploading PDFs and starting AI analysis...";
      })
      .addCase(oneClickCompare.fulfilled, (state, action) => {
        state.oneClickStatus = "done";
        state.oneClickStep = "";
        state.activeChecklist = action.payload.checklist;
        state.checklists = [action.payload.checklist, ...state.checklists];
        state.priorComparisonSummary = action.payload.comparisonSummary;
        state.priorAISummary = action.payload.aiSummary || "";
        // Add documents to the list
        if (action.payload.documents?.old) {
          state.documents = [...state.documents, action.payload.documents.old];
        }
        if (action.payload.documents?.new) {
          state.documents = [...state.documents, action.payload.documents.new];
        }
      })
      .addCase(oneClickCompare.rejected, (state, action) => {
        state.oneClickStatus = "error";
        state.oneClickStep = "";
        state.error = action.payload;
      })

      // Attach prior policy
      .addCase(attachPriorPolicy.fulfilled, (state, action) => {
        state.activeChecklist = action.payload;
        const idx = state.checklists.findIndex((c) => c._id === action.payload._id);
        if (idx >= 0) state.checklists[idx] = action.payload;
      })

      // Compare prior policy
      .addCase(comparePriorPolicy.pending, (state) => {
        state.priorCompareStatus = "comparing";
      })
      .addCase(comparePriorPolicy.fulfilled, (state, action) => {
        state.priorCompareStatus = "done";
        state.activeChecklist = action.payload.checklist;
        state.priorComparisonSummary = action.payload.summary;
        state.priorAISummary = action.payload.aiSummary || "";
        const idx = state.checklists.findIndex((c) => c._id === action.payload.checklist._id);
        if (idx >= 0) state.checklists[idx] = action.payload.checklist;
      })
      .addCase(comparePriorPolicy.rejected, (state, action) => {
        state.priorCompareStatus = "error";
        state.error = action.payload;
      })

      // Delete checklist
      .addCase(deleteChecklist.fulfilled, (state, action) => {
        state.checklists = state.checklists.filter((c) => c._id !== action.payload);
        if (state.activeChecklist?._id === action.payload) state.activeChecklist = null;
      });
  },
});

export const {
  clearError,
  clearActiveChecklist,
  clearActiveDocument,
  clearExportData,
  setActiveChecklist,
} = policyCheckSlice.actions;

export default policyCheckSlice.reducer;
